import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { createOrReuseThread, resolveRecipientIdentity, sendMessage, suggestRegisteredRecipients, type RecipientSuggestion } from '../../api/messenger'
import NavBar from '../../components/layout/NavBar'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'

export default function Compose() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const toParam = (params.get('to') || '').trim()
  const recipientParam = (params.get('recipient') || '').trim()
  const recipientIdParam = (params.get('recipient_id') || '').trim()
  const parsedRecipientId = Number(recipientIdParam)
  const initialRecipientId = Number.isFinite(parsedRecipientId) && parsedRecipientId > 0 ? parsedRecipientId : null

  const normalizedToParam = toParam.replace(/^@/, '').trim()
  const initialRecipientLabel =
    recipientParam || (normalizedToParam ? `@${normalizedToParam}` : initialRecipientId ? String(initialRecipientId) : '')
  const [recipientInput, setRecipientInput] = useState<string>(initialRecipientLabel)
  const [prefilledRecipientId, setPrefilledRecipientId] = useState<number | null>(initialRecipientId)
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<RecipientSuggestion[]>([])
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const authed = Boolean(getToken())
  const { me } = useMe(authed)

  const activeToken = useMemo(() => {
    const parts = recipientInput.split(',')
    const last = parts[parts.length - 1] ?? ''
    return last.trim()
  }, [recipientInput])

  const activeTokenQuery = useMemo(() => {
    return activeToken.replace(/^@/, '').trim()
  }, [activeToken])

  useEffect(() => {
    const q = activeTokenQuery
    if (!q || q.length < 2 || /^\d+$/.test(q)) {
      setSuggestions([])
      setSuggestionsOpen(false)
      setSuggestionsLoading(false)
      return
    }
    let cancelled = false
    const t = window.setTimeout(async () => {
      try {
        setSuggestionsLoading(true)
        const rows = await suggestRegisteredRecipients(q, 6)
        if (cancelled) return
        const next = rows
          .filter((r) => String(r.handle || '').trim().length > 0)
          .slice(0, 6)
        setSuggestions(next)
        setSuggestionsOpen(next.length > 0)
      } catch {
        if (!cancelled) {
          setSuggestions([])
          setSuggestionsOpen(false)
        }
      } finally {
        if (!cancelled) setSuggestionsLoading(false)
      }
    }, 180)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [activeTokenQuery])

  function applySuggestion(handleLike: string) {
    const clean = String(handleLike || '').replace(/^@/, '').trim()
    if (!clean) return
    const parts = recipientInput.split(',')
    if (parts.length === 0) {
      setRecipientInput(`@${clean}`)
    } else {
      parts[parts.length - 1] = ` @${clean}`
      setRecipientInput(parts.join(',').replace(/^ /, ''))
    }
    setSuggestionsOpen(false)
  }

  async function resolveMemberIds(): Promise<number[]> {
    const tokens = recipientInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)

    const out = new Set<number>()
    if (prefilledRecipientId != null && Number.isFinite(prefilledRecipientId) && prefilledRecipientId > 0) {
      out.add(prefilledRecipientId)
    }
    for (const token of tokens) {
      const numeric = Number(token)
      if (Number.isFinite(numeric) && numeric > 0) {
        out.add(numeric)
        continue
      }
      const normalized = token.replace(/^@/, '').trim()
      if (!normalized) continue
      const resolved = await resolveRecipientIdentity(normalized)
      if (!Number.isFinite(resolved.userId) || resolved.userId <= 0) {
        throw new Error(`User not found: ${token}`)
      }
      out.add(Number(resolved.userId))
    }
    return Array.from(out)
  }

  async function onSend() {
    if (submitting) return
    if (!recipientInput.trim() && prefilledRecipientId == null) {
      setError('Enter a username, handle, or user ID.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const memberIds = await resolveMemberIds()
      if (!memberIds.length) {
        setError("Couldn't resolve recipient. Enter a valid username/handle.")
        return
      }
      if (me?.id && memberIds.includes(me.id)) {
        setError('You cannot message yourself.')
        return
      }
      const { threadId } = await createOrReuseThread({ members: memberIds })
      const body = text.trim()
      if (body.length) {
        await sendMessage(threadId, { text: body })
      }
      navigate(`/inbox/i/${threadId}`)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not start chat')
    } finally {
      setSubmitting(false)
    }
  }

  const sendEnabled = recipientInput.trim().length > 0 && !submitting

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />

      <main className="mx-auto max-w-4xl px-4 py-4">
        <div className="mb-3">
          <Link to="/inbox" className="text-white/80 hover:text-white text-sm">← Back to inbox</Link>
        </div>
        <h1 className="text-xl font-extrabold mb-4">New Message</h1>

        {error && <div className="text-sm text-red-400 mb-3">{error}</div>}

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-white/80 mb-1">To</label>
            <input
              value={recipientInput}
              onChange={(e) => {
                const next = e.target.value
                setRecipientInput(next)
                setSuggestionsOpen(true)
                if (prefilledRecipientId != null && next.trim() !== initialRecipientLabel.trim()) {
                  setPrefilledRecipientId(null)
                }
              }}
              onFocus={() => {
                if (suggestions.length > 0) setSuggestionsOpen(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSuggestionsOpen(false)
                  return
                }
                if (e.key === 'Enter' && suggestionsOpen && suggestions.length > 0) {
                  e.preventDefault()
                  applySuggestion(suggestions[0].handle)
                }
              }}
              placeholder="@handle, username, or user ID"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand"
            />
            {suggestionsOpen && (
              <div className="mt-2 rounded-xl border border-white/10 bg-neutral-950/95 p-1 shadow-xl">
                {suggestionsLoading ? (
                  <div className="px-3 py-2 text-xs text-white/60">Searching...</div>
                ) : suggestions.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-white/60">No matching handles.</div>
                ) : (
                  suggestions.map((row) => (
                    <button
                      key={`${row.userId}-${row.handle}`}
                      type="button"
                      onClick={() => applySuggestion(row.handle)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-white/10"
                    >
                      <span className="truncate text-sm text-white/90">{row.displayName || row.username || row.handle}</span>
                      <span className="ml-2 shrink-0 text-xs text-white/65">{row.handle}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            {prefilledRecipientId != null ? (
              <div className="mt-1 text-xs text-white/60">Prefilled recipient ready.</div>
            ) : null}
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Message</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write something… (optional)"
              rows={4}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
          <div className="pt-2">
            <button
              onClick={onSend}
              disabled={!sendEnabled}
              className="rounded-lg bg-brand px-4 py-2 text-white font-semibold hover:bg-brand-600 disabled:opacity-60"
            >
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
